import { z } from "zod";
// EEX Research → Data: shared day-ahead (DAM) price zones. Zones are global
// market data; any company's admin or trader may request one and every user
// sees the result. Brain owns the zone catalogue (EICs, names, availability),
// so zone keys stay constrained strings rather than a shared enum.
// First delivery day of the separate DE-LU and AT bidding zones; every zone's
// history starts here.
export const EEX_DAM_HISTORY_START = "2018-10-01";
export const DAM_ZONE_GAP_RANGES_LIST_LIMIT = 50;
export const DAM_ZONE_GAP_RANGES_DETAIL_LIMIT = 500;
const IsoDate = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/);
const IsoMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
const IsoDateTime = z.string().datetime({ offset: true });
const Count = z.number().int().nonnegative();
export const DamZoneKeySchema = z.string().regex(/^[A-Z][A-Z0-9-]{1,11}$/);
export const DamZoneAvailabilitySchema = z.enum(["available", "partial", "unavailable"]);
// What a viewer sees for one zone, combining registry, ledger and job state.
export const DamZoneStateSchema = z.enum([
    "not_stored", "queued", "downloading", "validating", "paused",
    "stored_partial", "ready", "ready_with_gaps", "failed",
]);
export const DamZoneJobKindSchema = z.enum(["full_history", "gap_retry", "revalidate"]);
export const DamZoneJobPhaseSchema = z.enum([
    "queued", "downloading", "validating", "paused", "succeeded", "succeeded_with_gaps", "failed",
]);
export const DamZonePauseReasonSchema = z.enum(["rate_limited", "source_retry", "restarting"]);
export const DamZoneJobErrorCodeSchema = z.enum([
    "source_unavailable", "rate_limited", "token_invalid", "no_source_data", "pipeline_error",
]);
export const DamZoneValidationStateSchema = z.enum(["not_run", "passed", "passed_with_gaps", "failed"]);
export const DamZoneGapReasonSchema = z.enum(["source_missing", "source_partial", "source_currency", "source_anomaly"]);
export const DamZoneResolutionMinutesSchema = z.union([z.literal(15), z.literal(30), z.literal(60)]);
const ACTIVE_PHASES = new Set(["queued", "downloading", "validating", "paused"]);
export const DamZoneJobSchema = z.object({
    id: z.string().uuid(),
    kind: DamZoneJobKindSchema,
    phase: DamZoneJobPhaseSchema,
    pauseReason: DamZonePauseReasonSchema.nullable(),
    resumesAt: IsoDateTime.nullable(),
    queuePosition: z.number().int().positive().nullable(),
    monthsDone: Count,
    monthsTotal: Count,
    currentMonth: IsoMonth.nullable(),
    validationDone: Count,
    validationTotal: Count,
    percent: z.number().finite().min(0).max(100),
    etaSeconds: Count.nullable(),
    heartbeatAt: IsoDateTime.nullable(),
    errorCode: DamZoneJobErrorCodeSchema.nullable(),
    requestedByYourCompany: z.boolean(),
    createdAt: IsoDateTime,
    startedAt: IsoDateTime.nullable(),
    completedAt: IsoDateTime.nullable(),
}).strict()
    .refine((job) => job.monthsDone <= job.monthsTotal, { message: "monthsDone exceeds monthsTotal", path: ["monthsDone"] })
    .refine((job) => job.validationDone <= job.validationTotal, { message: "validationDone exceeds validationTotal", path: ["validationDone"] })
    .refine((job) => (job.phase === "paused") === (job.pauseReason !== null), { message: "only a paused job has a pause reason", path: ["pauseReason"] })
    .refine((job) => ACTIVE_PHASES.has(job.phase) === (job.completedAt === null), { message: "only finished jobs have completedAt", path: ["completedAt"] })
    .refine((job) => job.queuePosition === null || job.phase === "queued" || job.phase === "paused", { message: "only waiting jobs have a queue position", path: ["queuePosition"] })
    .refine((job) => job.errorCode === null || job.phase === "failed" || job.phase === "paused", { message: "only failed or paused jobs report an error", path: ["errorCode"] });
const DateRangeSchema = z.object({ from: IsoDate, to: IsoDate }).strict()
    .refine((range) => range.from <= range.to, { message: "range ends before it starts", path: ["to"] });
export const DamZoneGapRangeSchema = z.object({ from: IsoDate, to: IsoDate, reason: DamZoneGapReasonSchema }).strict()
    .refine((range) => range.from <= range.to, { message: "range ends before it starts", path: ["to"] });
export const DamZoneResolutionSegmentSchema = z.object({ from: IsoDate, to: IsoDate, minutes: DamZoneResolutionMinutesSchema }).strict()
    .refine((segment) => segment.from <= segment.to, { message: "segment ends before it starts", path: ["to"] });
export const DamZoneCoverageSchema = z.object({
    firstDate: IsoDate,
    lastDate: IsoDate,
    daysStored: Count,
    daysKnownGap: Count,
    daysPending: Count,
    // True once the full-history download from EEX_DAM_HISTORY_START finished.
    fullHistory: z.boolean(),
    // Native ENTSO-E resolution over time; hourly days are stored on the 15-minute grid.
    resolutionSegments: z.array(DamZoneResolutionSegmentSchema),
}).strict()
    .refine((coverage) => coverage.firstDate <= coverage.lastDate, { message: "coverage ends before it starts", path: ["lastDate"] });
// Monthly DAM average vs the EEX base future's final settlement. Informational:
// it never changes the validation verdict.
export const DamZoneFuturesCheckSchema = z.object({
    family: z.string().min(1),
    firstMonth: IsoMonth,
    monthsCompared: Count,
    monthsMatched: Count,
    monthsMismatched: Count,
    toleranceEurMwh: z.number().finite().positive(),
}).strict()
    .refine((check) => check.monthsMatched + check.monthsMismatched === check.monthsCompared, {
    message: "matched and mismatched months must add up", path: ["monthsCompared"],
});
export const DamZoneValidationSchema = z.object({
    state: DamZoneValidationStateSchema,
    validatedAt: IsoDateTime.nullable(),
    checked: DateRangeSchema.nullable(),
    gapRanges: z.array(DamZoneGapRangeSchema).max(DAM_ZONE_GAP_RANGES_LIST_LIMIT),
    gapRangesTruncated: z.boolean(),
    flagCounts: z.object({
        outOfBounds: Count,
        a03Filled: Count,
        unexpectedResolution: Count,
        duplicateSeriesResolved: Count,
        // Days ENTSO-E published in a local currency, converted to EUR.
        currencyConverted: Count,
    }).strict(),
    futuresCheck: DamZoneFuturesCheckSchema.nullable(),
    futuresCheckUnavailableReason: z.enum(["no_eex_reference", "no_settlement_history"]).nullable(),
}).strict()
    .refine((validation) => (validation.futuresCheck === null) !== (validation.futuresCheckUnavailableReason === null), {
    message: "give either a futures check or the reason it is unavailable", path: ["futuresCheckUnavailableReason"],
});
const DamZoneSummaryObject = z.object({
    key: DamZoneKeySchema,
    code: z.string().min(1).max(16),
    name: z.string().min(1).max(80),
    countryCode: z.string().regex(/^[A-Z]{2}$/),
    mapShapeId: z.string().min(1).nullable(),
    availability: DamZoneAvailabilitySchema,
    unavailableReason: z.string().min(1).nullable(),
    notes: z.array(z.string().min(1)),
    eexFamilies: z.array(z.string().min(1)),
    state: DamZoneStateSchema,
    coverage: DamZoneCoverageSchema.nullable(),
    validation: DamZoneValidationSchema.nullable(),
    job: DamZoneJobSchema.nullable(),
}).strict();
const STORED_STATES = new Set(["stored_partial", "ready", "ready_with_gaps"]);
const JOB_STATES = new Set(["queued", "downloading", "validating", "paused"]);
function summaryIsConsistent(zone, ctx) {
    if ((zone.availability === "unavailable") !== (zone.unavailableReason !== null)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "only unavailable zones carry a reason", path: ["unavailableReason"] });
    }
    if (zone.availability === "unavailable" && zone.state !== "not_stored") {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "an unavailable zone cannot be stored", path: ["state"] });
    }
    if (STORED_STATES.has(zone.state) && zone.coverage === null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "a stored zone reports its coverage", path: ["coverage"] });
    }
    if (zone.state === "not_stored" && (zone.coverage !== null || zone.job !== null)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "a zone that is not stored has no coverage or job", path: ["state"] });
    }
    if (JOB_STATES.has(zone.state) && (zone.job === null || !ACTIVE_PHASES.has(zone.job.phase))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "an in-progress zone reports its active job", path: ["job"] });
    }
    if (zone.job !== null && ACTIVE_PHASES.has(zone.job.phase) && !JOB_STATES.has(zone.state)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "an active job puts the zone in progress", path: ["state"] });
    }
}
export const DamZoneSummarySchema = DamZoneSummaryObject.superRefine(summaryIsConsistent);
export const DamZoneRequestBlockedReasonSchema = z.enum(["downloads_disabled", "no_entsoe_token", "not_permitted"]);
export const DamZoneListResponseSchema = z.object({
    generatedAt: IsoDateTime,
    historyStart: z.literal(EEX_DAM_HISTORY_START),
    canRequest: z.boolean(),
    requestBlockedReason: DamZoneRequestBlockedReasonSchema.nullable(),
    activeJobs: Count,
    zones: z.array(DamZoneSummarySchema),
}).strict()
    .refine((list) => list.canRequest === (list.requestBlockedReason === null), {
    message: "a blocked request states its reason", path: ["requestBlockedReason"],
})
    .refine((list) => new Set(list.zones.map((zone) => zone.key)).size === list.zones.length, {
    message: "zone keys must be unique", path: ["zones"],
});
export const DamZoneFuturesMismatchSchema = z.object({
    month: IsoMonth,
    family: z.string().min(1),
    finalSettlementEurMwh: z.number().finite(),
    damAverageEurMwh: z.number().finite(),
    differenceEurMwh: z.number().finite(),
}).strict();
export const DamZoneDetailResponseSchema = DamZoneSummaryObject.extend({
    gapRanges: z.array(DamZoneGapRangeSchema).max(DAM_ZONE_GAP_RANGES_DETAIL_LIMIT),
    gapRangesTruncated: z.boolean(),
    futuresMismatches: z.array(DamZoneFuturesMismatchSchema),
}).strict().superRefine(summaryIsConsistent);
export const DamZoneRequestBodySchema = z.object({ zoneKey: DamZoneKeySchema }).strict();
// The server decides whether a request starts, joins, retries or is a no-op.
export const DamZoneRequestOutcomeSchema = z.enum(["queued", "coalesced", "retry_queued", "gap_retry_queued", "already_available"]);
export const DamZoneRequestResponseSchema = z.object({
    outcome: DamZoneRequestOutcomeSchema,
    zone: DamZoneSummarySchema,
}).strict()
    .refine((response) => response.outcome === "already_available" || response.zone.job !== null, {
    message: "an accepted request reports the job", path: ["zone", "job"],
});
// Errors from the Research routes themselves. Session, role and capability
// guards answer 401/403 with their own generic { error, code } bodies (for
// example eex_capability_denied); clients must not parse those with this schema.
export const DamZoneRequestErrorCodeSchema = z.enum([
    "invalid_request", "unknown_zone", "zone_unavailable", "retry_cooldown", "downloads_disabled", "no_entsoe_token", "not_permitted",
]);
export const DamZoneRequestErrorSchema = z.object({
    error: z.string().min(1),
    code: DamZoneRequestErrorCodeSchema,
    retryAfterSeconds: z.number().int().positive().optional(),
}).strict()
    .refine((body) => body.code !== "retry_cooldown" || body.retryAfterSeconds !== undefined, {
    message: "a cooldown says when to retry", path: ["retryAfterSeconds"],
});
//# sourceMappingURL=eex-research-contracts.js.map